// Pure formatting rules (spec §2.5). Money is integer minor units: never divided as a float.

const pow10 = (n: number): bigint => 10n ** BigInt(n);

/** Decimal digits of a currency per Intl (JPY 0, USD 2, BHD 3); 2 for codes Intl does not know. */
export function currencyDecimals(currency: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** Minor units → exact decimal string ("-1234" USD → "-12.34"). */
export function minorToDecimal(amount: number | bigint | string, decimals: number): string {
  const v = BigInt(amount);
  const neg = v < 0n;
  const abs = neg ? -v : v;
  const int = abs / pow10(decimals);
  const frac = (abs % pow10(decimals)).toString().padStart(decimals, "0");
  return `${neg ? "-" : ""}${int}${decimals ? `.${frac}` : ""}`;
}

/**
 * "-1234","USD" → "-$12.34". Platform minor units are 1/100 in every currency (2000 JPY minor = ¥20); the currency's
 * own decimals, capped at 2, only set how many are shown. Unknown currency codes get the code as suffix.
 */
export function formatMoney(amount: number | bigint | string, currency: string, locale = "en-US"): string {
  const decimals = Math.min(currencyDecimals(currency), 2);
  // Intl formats decimal strings exactly (no float round-trip).
  const value = minorToDecimal(amount, 2) as unknown as number;
  const opts = { minimumFractionDigits: decimals, maximumFractionDigits: decimals, signDisplay: "auto" } as const;
  try {
    return new Intl.NumberFormat(locale, { ...opts, style: "currency", currency, currencySign: "standard" }).format(
      value,
    );
  } catch {
    return `${new Intl.NumberFormat(locale, opts).format(value)} ${currency}`;
  }
}

/** 1500 → "15.00 %". */
export function formatBps(bps: number): string {
  return `${bpsToPercent(bps)} %`;
}

/** 1525 → "15.25" (two decimals, no float maths). */
export function bpsToPercent(bps: number): string {
  const sign = bps < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(bps));
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/** "15.25" → 1525; null when not a 0–100 percentage with at most two decimals. */
export function percentToBps(input: string | number): number | null {
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(String(input).trim());
  if (!m) return null;
  const bps = Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
  return bps <= 10_000 ? bps : null;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO date-time → "2026-11-01 13:05 UTC". */
export function formatUtc(value: string | Date, withTime = true): string {
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  const day = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  return withTime ? `${day} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC` : `${day} UTC`;
}

/** UTC calendar date of `now` + `days`, as "YYYY-MM-DD". */
export function utcDay(now: Date, days = 0): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days));
  return d.toISOString().slice(0, 10);
}

/** Commercial terms apply from a future UTC midnight: earliest tomorrow (spec §0, §7). */
export function isValidEffectiveDate(day: string, now: Date): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= utcDay(now, 1);
}
